import { useState } from "react";
import { PHONE_ERROR, isValidPhone, sanitizePhoneInput } from "@/lib/phone";

/**
 * Shared phone input — enforces the app-wide 10-digit rule.
 * Renders the *existing* `.lux-input` styling so no visual redesign is introduced;
 * caller can override `className` to keep parity with each screen.
 *
 * Props (in addition to any other <input> props):
 *   value:     controlled string
 *   onChange:  called with the *sanitised* string (digits only, ≤10)
 *   required:  whether empty is treated as invalid (default true)
 *   testId:    forwarded as data-testid on the input; error uses `${testId}-error`
 *   showError: if true, always show the error message when invalid.
 *              Defaults to only-after-touch behaviour.
 *
 * The component never changes the outer layout — the error is a small
 * caption underneath so it fits inside every existing form.
 */
export default function PhoneInput({
  value = "",
  onChange,
  required = true,
  className = "lux-input",
  placeholder = "10-digit mobile number",
  testId,
  showError = false,
  ...rest
}) {
  const [touched, setTouched] = useState(false);
  const clean = sanitizePhoneInput(value);
  const isEmpty = clean.length === 0;
  const invalid = required ? !isValidPhone(clean) : (!isEmpty && !isValidPhone(clean));
  const shouldShow = (showError || touched) && invalid && !isEmpty;
  // When required, also show the error once the user has interacted with the field.
  const shouldShowRequired = (showError || touched) && required && isEmpty;

  const handleChange = (e) => {
    const next = sanitizePhoneInput(e.target.value);
    onChange?.(next);
  };

  return (
    <>
      <input
        {...rest}
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        maxLength={10}
        pattern="[0-9]{10}"
        value={clean}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        placeholder={placeholder}
        className={className}
        aria-invalid={shouldShow || shouldShowRequired ? true : undefined}
        data-testid={testId}
      />
      {(shouldShow || shouldShowRequired) && (
        <div
          className="mt-1.5 text-[11.5px] text-nexa-danger"
          data-testid={testId ? `${testId}-error` : undefined}
        >
          {PHONE_ERROR}
        </div>
      )}
    </>
  );
}
